import React from 'react';

import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useScrollSpy } from 'pages/knz/useScrollSpy';

import { KnzTocItem } from './KnzViewer';

type Props = {
  /** 目次項目（KnzViewer が Section から収集したもの） */
  items: KnzTocItem[];
  /** 目次見出し（省略時は「目次」） */
  heading?: string;
};

/** 要素から見て、スクロール可能（overflow が auto / scroll）な最初の祖先を返す */
const findScrollParent = (el: HTMLElement | null): HTMLElement | null => {
  let node = el?.parentElement ?? null;
  while (node) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
};

/**
 * 記事公開ページ右カラムのスクロール連動目次（scrollspy）。
 * knz リファレンス（KnzReference）の右ペイン目次と同じ挙動。
 * スクロール位置に応じて現在の見出しをハイライトし、クリックで該当 Section へジャンプする。
 * 実際にスクロールするコンテナ（レイアウトの overflow:auto な祖先）を自動検出して監視する。
 */
export const KnzTocSidebar: React.FC<Props> = ({ items, heading = '目次' }) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const anchors = React.useMemo(() => items.map((i) => i.anchor), [items]);

  // マウント後にスクロールコンテナ（overflow:auto な祖先）を検出する。
  // window は実際にはスクロールしないため、これを useScrollSpy に渡す必要がある。
  const [scrollContainer, setScrollContainer] = React.useState<HTMLElement | null>(null);
  React.useEffect(() => {
    setScrollContainer(findScrollParent(rootRef.current));
  }, [items]);

  const activeId = useScrollSpy(anchors, scrollContainer);

  if (items.length === 0) return null;

  const scrollTo = (anchor: string) => {
    document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box
      ref={rootRef}
      sx={{
        // 親（ArticleDetail の目次カラム）が sticky で位置を固定する。
        // ここでは目次が長い場合に内部スクロールできるよう高さだけ制限する。
        maxHeight: 'calc(100vh - 96px)',
        overflow: 'auto',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 1, py: 1, mb: 0.5 }}>
        <ListAltOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          {heading}
        </Typography>
      </Stack>
      <Stack spacing={0}>
        {items.map((item) => {
          const active = activeId === item.anchor;
          return (
            <Link
              key={item.anchor}
              href={`#${item.anchor}`}
              underline="none"
              onClick={(e) => {
                e.preventDefault();
                scrollTo(item.anchor);
              }}
              sx={{
                display: 'block',
                py: 0.5,
                pl: item.isSub ? 2.5 : 1.5,
                pr: 1,
                borderLeft: 2,
                borderColor: active ? 'primary.main' : 'transparent',
                bgcolor: active ? 'action.selected' : 'transparent',
                transition: 'background-color 0.15s, border-color 0.15s',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography
                variant="body2"
                fontWeight={active ? 600 : 400}
                color={active ? 'primary.main' : 'text.secondary'}
                sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}
              >
                {item.label}
              </Typography>
            </Link>
          );
        })}
      </Stack>
    </Box>
  );
};

export default KnzTocSidebar;
